/* © CNES */
import { TreeExpandedKeysType } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { useContext, useMemo } from 'react';
import { productTypesNames } from '../assets/config/filters/Referenciel';
import { IDENTIFIER } from '../components/shared/Constants';
import { formatCollectionDetails } from '../components/shared/FormatProductDetails';
import { buildLevelName, buildSatelliteName } from '../components/shared/Utils';
import { FilterDataContext } from '../context/FilterDataContext';
import { Feature } from '../models/Feature';
import { ItemsAvailabilities } from '../models/ItemAvailability';
import { SatelliteDataItem } from '../models/SatelliteDataItem';

/**
 * Returns a sorted list of satellite collections.
 *
 * This function processes a list of features and maps them into collections with metadata such as
 * title, level, and satellite name. Collections are sorted by satellite name and specific levels
 * (e.g., SLC, GRD, OCN) based on predefined priorities.
 *
 * @param features - The list of features to process into collections.
 * @returns A sorted array of satellite collections.
 */
export const useSortedSatelliteCollections = (features: Feature[]) => {
  const { filterData } = useContext(FilterDataContext);
  return useMemo(() => {
    const collections = features?.map((feature: Feature) => ({
      title: feature.id,
      level: buildLevelName(feature),
      satellite: buildSatelliteName(feature, filterData),
    }));

    // Sort collections by satellite name and then by specific level priorities.
    collections?.sort((a, b) => a?.satellite?.localeCompare(b?.satellite));
    collections?.sort((a, b) => {
      if (a.level === productTypesNames.SLC) {
        return -1;
      }

      if (
        a.level === productTypesNames.GRD &&
        b.level === productTypesNames.SLC
      ) {
        return 1;
      }

      if (
        a.level === productTypesNames.GRD &&
        b.level === productTypesNames.OCN
      ) {
        return -1;
      }

      if (a.level === productTypesNames.OCN) {
        return 1;
      }

      return 0;
    });

    return collections;
  }, [features, filterData]);
};

/**
 * Processes a list of features into detailed collection objects with storage metadata.
 *
 * This function combines information from features and availability data to generate
 * collections with storage mode details (e.g., Datalake Tier 2 or Tier 3).
 *
 * @param features - The list of features to process.
 * @param itemsAvailabilities - The availability information for items within the features.
 * @returns A processed list of collection details with storage information.
 */
export const useFormattedCollections = (
  features: Feature[],
  itemsAvailabilities: ItemsAvailabilities | undefined
) => {
  return useMemo(() => {
    const collectionsDetails = features?.map((feature) => {
      const itemToCheck = itemsAvailabilities?.products?.find(
        (productAvailability) => productAvailability.id === feature.id
      );
      
      // Fix while tier 3 notifier not op
      //const itemAvailability = true
      const itemAvailability = itemToCheck?.files?.every(
        (file) => file.available === true
      );


      const collectionWithStorage = {
        ...feature,
        ...(itemAvailability !== undefined && {
          storage: {
            mode: itemAvailability ? 'Datalake Tier 2' : 'Datalake Tier 3',
          },
        }),
      };
      return formatCollectionDetails(collectionWithStorage);
    });

    return collectionsDetails;
  }, [features, itemsAvailabilities]);
};

/**
 * Converts a collection array into a hierarchical tree structure for a tree view.
 *
 * Each collection is organized by satellite, then by level, and finally by title.
 * This structure is used to create a tree of nodes compatible with the Tree component.
 *
 * @param data - The list of collections to convert into a tree.
 * @returns A hierarchical array of tree nodes.
 */
export const useDataToTree = (
  data: SatelliteDataItem[] | undefined
): { treeNodes: TreeNode[]; keysToExpand: TreeExpandedKeysType } => {
  if (!data) return { treeNodes: [], keysToExpand: {} };
  const tree: Record<string, TreeNode> = {};
  let keysToExpand: TreeExpandedKeysType = {};

  data.forEach(({ title, level, satellite }) => {
    // If the satellite does not exist in the tree, add it.
    if (!tree[satellite]) {
      tree[satellite] = {
        key: satellite,
        label: satellite,
        children: [],
      };
    }

    keysToExpand = { ...keysToExpand, [satellite]: true };

    const satelliteNode = tree[satellite];
    let levelNode = satelliteNode.children?.find((node) => node.key === level);

    // If the level does not exist under the satellite, add it.
    if (!levelNode) {
      levelNode = {
        key: level,
        label: level,
        children: [],
      };
      satelliteNode.children?.push(levelNode);
    }

    // Add the title to the appropriate level.
    levelNode.children?.push({
      key: title,
      label: title,
      children: undefined,
    });
  });
  // Convert the tree object into an array.
  const treeNodes = Object.values(tree);

  //return the list of nodes and the keys to expand in tree
  return { treeNodes, keysToExpand };
};
